import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { EconChart } from "@/components/tools/econ-micro/curve-chart";
import type { GiniCoefficientStrings } from "@/components/tools/gini-coefficient-calculator";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { minus } from "@/lib/tool-kit/econ-micro/format";
import {
  type Gini,
  type LorenzPoint,
  thin,
} from "@/lib/tool-kit/econ-micro/inequality";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

/** The Lorenz curve, the line of equality and the gap between them. */
export function LorenzGraph({
  points,
  gini,
  strings: s,
  fmt,
}: {
  points: readonly LorenzPoint[];
  gini: number;
  strings: GiniCoefficientStrings;
  fmt: Fmt;
}) {
  const drawn = thin(points, 400);
  const text = (v: number) => fmt(v, { maximumFractionDigits: 2 });
  const head = [s.axisPeople, s.axisIncome];
  const rows = points.map((p) => [text(p[0]), text(p[1])]);
  const showTable = points.length <= 101;
  return (
    <ChartFigure
      apa={false}
      data={toTsv([head, ...points.map((p) => [p[0], p[1]])])}
      figureNumber="1"
      filename={chartFilename(s.graphTitle, "lorenz-curve")}
      labels={s}
      note=""
      table={showTable ? { head, rows } : null}
      title={s.graphTitle}
    >
      <EconChart
        curves={[
          {
            id: "equality",
            points: [
              [0, 0],
              [100, 100],
            ],
            color: CHART_INK.axis,
            dashed: true,
            label: s.curveEquality,
          },
          {
            id: "lorenz",
            points: drawn,
            color: CHART_COLORS[0],
            label: s.curveLorenz,
            labelAtIndex: Math.floor(drawn.length * 0.75),
          },
        ]}
        description={t(s.graphDescription, {
          gini: fmt(gini, { maximumFractionDigits: 3 }),
        })}
        fmt={fmt}
        id="gc-chart"
        label={s.graphTitle}
        legend={[{ label: s.legendArea, color: CHART_COLORS[3] }]}
        shades={[
          { id: "area", color: CHART_COLORS[3], opacity: 0.3, points: drawn },
        ]}
        xMax={100}
        xTitle={s.axisPeople}
        yMax={100}
        yTitle={s.axisIncome}
      />
    </ChartFigure>
  );
}

/** The sorted values and the two forms of the formula, with the user's numbers in them. */
export function IncomeWorking({
  result,
  strings: s,
  fmt,
}: {
  result: Gini;
  strings: GiniCoefficientStrings;
  fmt: Fmt;
}) {
  const n = (v: number, digits = 4) =>
    minus(fmt(v, { maximumFractionDigits: digits }));
  const short = result.sorted.length <= 12;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.workingHeading}</h3>
      <ul className="space-y-1 font-mono text-foreground text-sm">
        {short ? (
          <li>
            {t(s.workSorted, {
              values: result.sorted.map((v) => n(v, 2)).join(s.listJoin),
            })}
          </li>
        ) : null}
        <li>
          {t(s.workWeighted, {
            sum: n(result.weightedSum, 2),
          })}
        </li>
        <li>
          {t(s.workGini, {
            sum: n(result.weightedSum, 2),
            n: n(result.n, 0),
            total: n(result.total, 2),
            gini: n(result.gini),
          })}
        </li>
        <li>
          {t(s.workCorrected, {
            gini: n(result.gini),
            n: n(result.n, 0),
            corrected: n(result.corrected ?? 0),
          })}
        </li>
      </ul>
      <p className="text-muted-foreground text-xs">{s.workNote}</p>
    </div>
  );
}

export function IncomeStats({
  result,
  strings: s,
  fmt,
}: {
  result: Gini;
  strings: GiniCoefficientStrings;
  fmt: Fmt;
}) {
  const n = (v: number, digits = 4) =>
    fmt(v, { maximumFractionDigits: digits });
  return (
    <StatGrid>
      <StatCard emphasis label={s.statGini} value={n(result.gini)} />
      <StatCard label={s.statCorrected} value={n(result.corrected ?? 0)} />
      <StatCard label={s.statMaximum} value={n(result.maximum)} />
      <StatCard label={s.statMean} value={n(result.mean, 2)} />
      <StatCard label={s.statCount} value={n(result.n, 0)} />
    </StatGrid>
  );
}

export function GroupTable({
  rows,
  strings: s,
}: {
  rows: (string | number)[][];
  strings: GiniCoefficientStrings;
}) {
  return (
    <DataTable
      head={[
        s.columnPopulation,
        s.columnIncome,
        s.columnCumPop,
        s.columnCumIncome,
      ]}
      rows={rows}
    />
  );
}
